
import React, { useState, useMemo } from 'react'
import { Stack, Typography } from '@mui/material'
import { Modal, Button } from '@repo/ui'
import { Select, type SelectItem } from 'components'
import { useTags, useProducts, useCertificateMutations } from 'hooks/api'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'

interface CreateCertificateDevModalProps {
  open: boolean
  onClose?: () => void
  refetch?: () => void
}

export const CreateCertificateDevModal = (props: CreateCertificateDevModalProps) => {
  const { open, onClose, refetch } = props
  const [selectedTagId, setSelectedTagId] = useState<string>('')
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const { open: notification } = useSnackbar()

  const { data: tagsData, isLoading: isLoadingTags } = useTags(
    { limit: 100 },
    { enabled: open }
  )

  const { data: productsData, isLoading: isLoadingProducts } = useProducts(
    { limit: 100 },
    { enabled: open }
  )

  const [{ createWithoutScanSession }, { isCreatingWithoutScanSession }] = useCertificateMutations({
    onSuccess: () => {
      handleClose()
      refetch?.()
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message || 'Failed to create certificate',
      })
    },
  })

  const tagOptions: SelectItem[] = useMemo(() => {
    if (!tagsData?.data) return []
    return tagsData.data.map((tag) => ({
      label: tag.uid,
      value: tag.uid,
    }))
  }, [tagsData])

  const productOptions: SelectItem[] = useMemo(() => {
    if (!productsData?.data) return []
    return productsData.data.map((product) => ({
      label: product.title || product.sku || product.id,
      value: product.id,
    }))
  }, [productsData])

  const handleClose = () => {
    setSelectedTagId('')
    setSelectedProductId('')
    onClose?.()
  }

  const handleCreate = () => {
    if (!selectedTagId || !selectedProductId) {
      notification({
        type: 'error',
        title: 'Please select both a tag and a product',
      })
      return
    }

    createWithoutScanSession({
      product_id: selectedProductId,
      tag_uids: [selectedTagId],
    })
  }

  const isDisabled = !selectedTagId || !selectedProductId || isCreatingWithoutScanSession

  return (
    <Modal
      title="Create certificate (No Scan Session)"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isCreatingWithoutScanSession}
          >
            Cancel
          </Button>
          <Button
            disableElevation
            onClick={handleCreate}
            disabled={isDisabled}
            loading={isCreatingWithoutScanSession}
          >
            Create Certificate
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body3" color="text.secondary">
          This feature is only available in development mode. Select a tag and product to create a certificate.
        </Typography>

        <Select
          label="Tag"
          options={tagOptions}
          value={selectedTagId}
          onChange={(value) => setSelectedTagId(value as string)}
          placeholder="Select a tag"
          disabled={isLoadingTags || isCreatingWithoutScanSession}
        />

        <Select
          label="Product"
          options={productOptions}
          value={selectedProductId}
          onChange={(value) => setSelectedProductId(value as string)}
          placeholder="Select a product"
          disabled={isLoadingProducts || isCreatingWithoutScanSession}
        />
      </Stack>
    </Modal>
  )
}
